import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { EAccountRole } from "@rentiohq/shared-frontend/dist/types/auth.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  Modal,
  SegmentedControl,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import { IEnabledAccount } from "components/EnabledAccountsSelect/EnabledAccountsSelect";
import React from "react";

interface IProps {
  employee: IEnabledAccount;
  onClose: () => void;
  onSuccess: () => void;
}

export const ChangeRoleModal = (props: IProps) => {
  const { employee, onClose, onSuccess } = props;

  const currentRole = employee.accountRoles?.includes(EAccountRole.OfficeAdmin)
    ? EAccountRole.OfficeAdmin
    : EAccountRole.Employee;

  const [role, setRole] = React.useState<EAccountRole>(currentRole);
  const [isLoading, setIsLoading] = React.useState(false);

  const options = [
    {
      id: EAccountRole.OfficeAdmin,
      value: getLocalizedText("account_role.office_admin"),
    },
    {
      id: EAccountRole.Employee,
      value: getLocalizedText("account_role.employee"),
    },
  ];

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await api.patch(`/accounts/employees/${employee.userId}/account-role`, {
        isV2: true,
        data: { roles: [role] },
      });
      onSuccess();
    } catch (unknownError) {
      const error = unknownError as any;
      showAlert({
        type: "error",
        message:
          error?.response?.data?.message ||
          getLocalizedText("account_role.error.generic"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      heading={getLocalizedText("account_role.modal.heading", {
        name: getName(employee),
      })}
      onClose={onClose}
      shouldCloseOnOverlayClick={true}
      actions={[
        {
          content: getLocalizedText("system.cancel"),
          appearance: "outline",
          isDisabled: isLoading,
          onClick: onClose,
        },
        {
          content: getLocalizedText("account_role.action.change_role"),
          appearance: "primary",
          isDisabled: isLoading || role === currentRole,
          onClick: handleConfirm,
        },
      ]}
    >
      <TextStyle variation="subdued">
        {getLocalizedText("account_role.modal.description")}
      </TextStyle>

      <SegmentedControl
        name="accountRole"
        selected={role}
        options={options}
        onChange={(id: EAccountRole) => setRole(id)}
      />

      {role === EAccountRole.Employee && (
        <Banner
          title={getLocalizedText("account_role.employee_banner.title")}
          icon="alertDiamond"
          variation="warning"
          hasDismiss={false}
        >
          <TextStyle>
            {getLocalizedText("account_role.employee_banner.content", {
              name: getName(employee),
            })}
          </TextStyle>
        </Banner>
      )}

      {role === EAccountRole.OfficeAdmin && (
        <Banner
          title={getLocalizedText("account_role.admin_banner.title")}
          icon="checkBadge"
          variation="success"
          hasDismiss={false}
        >
          <TextStyle>
            {getLocalizedText("account_role.admin_banner.content", {
              name: getName(employee),
            })}
          </TextStyle>
        </Banner>
      )}
    </Modal>
  );
};

// eslint-disable-next-line import/no-default-export
export default ChangeRoleModal;
